// Buyer-side directory. Mirrors the seller service shape but the data
// model represents wanted-to-buy listings: per-segment quantity + max
// rate + urgency, optional battle context, total budget.

import {
  collection, query, orderBy, limit, getDocs, getDoc, doc, startAfter,
  onSnapshot,
} from "firebase/firestore";
import { db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { isDemoMode } from "./mode";

const COL_LIVE = "pop_market_buyers";
const COL_SEED = "pop_market_buyers_seed";
function colRead() { return isDemoMode() ? COL_SEED : COL_LIVE; }

// Single buyer by handle
export async function getBuyerByHandle(handle) {
  if (!handle) return null;
  const snap = await getDoc(doc(db, colRead(), handle.toLowerCase()));
  return snap.exists() ? { handle: snap.id, ...snap.data() } : null;
}

export function watchBuyerByHandle(handle, onChange) {
  if (!handle) return () => {};
  return onSnapshot(doc(db, colRead(), handle.toLowerCase()), (snap) => {
    onChange(snap.exists() ? { handle: snap.id, ...snap.data() } : null);
  });
}

// Directory query — same index-free strategy as querySellers:
// pure orderBy + limit server-side, all filters client-side.
//
//   game, region, segment, urgency, sort, pageSize, cursor
export async function queryBuyers(opts = {}) {
  const {
    game, region, segment, urgency,
    sort = "newest", pageSize = 24, cursor = null,
  } = opts;

  const clauses = [
    orderBy("createdAt", "desc"),
    limit(Math.max(pageSize * 4, 60)),
  ];
  if (cursor) clauses.splice(clauses.length - 1, 0, startAfter(cursor));
  if (sort === "biggestBudget") {
    clauses[0] = orderBy("budgetINR", "desc");
  }

  const snap = await getDocs(query(collection(db, colRead()), ...clauses));
  const raw = snap.docs.map((d) => ({ handle: d.id, ...d.data() }));

  const filtered = raw.filter((b) => {
    if (b.status !== "active") return false;
    if (b.paused) return false;
    if (game && b.game !== game) return false;
    if (region && b.region !== region) return false;
    if (segment) {
      const need = b.needs?.[segment];
      if (!need || !need.quantity || Number(need.quantity) <= 0) return false;
    }
    if (urgency) {
      const anyMatches = Object.values(b.needs || {})
          .some((n) => n?.urgency === urgency);
      if (!anyMatches) return false;
    }
    return true;
  }).slice(0, pageSize);

  return {
    buyers: filtered,
    nextCursor: snap.docs[snap.docs.length - 1] || null,
    done: raw.length < clauses[clauses.length - 1].limit,
  };
}

// Server-side spam wall callable. Mirror of popMarketSubmitListing but
// for buyer requirements. (Not deployed yet — front-end has the form
// scaffolded for when we wire the buyer signup flow.)
export async function submitBuyerListing(payload) {
  return httpsCallable(functions, "popMarketSubmitBuyer")(payload)
      .then((r) => r.data);
}
